import React, { useState, useEffect } from "react";
import { ComplianceTypeEntity } from "../../domain/entities/ComplianceTypeEntity";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";

interface ComplianceTypeFormProps {
  initialData?: ComplianceTypeEntity | null;
  onSubmit: (data: Partial<ComplianceTypeEntity>) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const ComplianceTypeForm: React.FC<ComplianceTypeFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    compliance_type: "",
    compliance_name: "",
    authority: "",
    frequency: "",
    description: "",
    is_active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        compliance_type: initialData.compliance_type || "",
        compliance_name: initialData.compliance_name || "",
        authority: initialData.authority || "",
        frequency: initialData.frequency || "",
        description: initialData.description || "",
        is_active: initialData.is_active ?? true,
      });
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number"
          ? parseInt(value) || 0
          : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.compliance_type.trim()) {
      newErrors.compliance_type = "Compliance type is required";
    }
    if (!formData.compliance_name.trim()) {
      newErrors.compliance_name = "Compliance name is required";
    }
    if (!formData.authority.trim()) {
      newErrors.authority = "Authority is required";
    }
    if (!formData.frequency.trim()) {
      newErrors.frequency = "Frequency is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit(formData);
    } catch (err: any) {
      alert(err.message || "Failed to save compliance type");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-left">
      <TextField
        label="Compliance Type *"
        helpText={
          errors.compliance_type ||
          "Unique identifier (e.g., GST_MONTHLY, TDS_QUARTERLY, PF_MONTHLY)"
        }
        error={!!errors.compliance_type}
      >
        <TextField.Input
          name="compliance_type"
          value={formData.compliance_type}
          onChange={handleChange}
          placeholder="e.g., GST_MONTHLY, TDS_QUARTERLY"
          disabled={!!initialData || loading} // Cannot edit compliance_type once created
        />
      </TextField>

      <TextField
        label="Compliance Name *"
        helpText={errors.compliance_name || ""}
        error={!!errors.compliance_name}
      >
        <TextField.Input
          name="compliance_name"
          value={formData.compliance_name}
          onChange={handleChange}
          placeholder="e.g., GST Monthly Return"
          disabled={loading}
        />
      </TextField>

      <TextField
        label="Authority *"
        helpText={errors.authority || ""}
        error={!!errors.authority}
      >
        <TextField.Input
          name="authority"
          value={formData.authority}
          onChange={handleChange}
          placeholder="e.g., GST, TDS, PF, ESI"
          disabled={loading}
        />
      </TextField>

      <div className="flex flex-col gap-2 text-left">
        <label className="text-body-bold font-body-bold text-default-font text-left">
          Frequency <span className="text-red-500">*</span>
        </label>
        <select
          name="frequency"
          value={formData.frequency}
          onChange={handleChange}
          disabled={loading}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left"
        >
          <option value="">Select frequency</option>
          <option value="MONTHLY">Monthly</option>
          <option value="QUARTERLY">Quarterly</option>
          <option value="ANNUAL">Annual</option>
          <option value="AD_HOC">Ad Hoc</option>
        </select>
        {errors.frequency && (
          <span className="text-caption font-caption text-red-600 text-left">
            {errors.frequency}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2 text-left">
        <label className="text-body-bold font-body-bold text-default-font text-left">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Optional description"
          rows={3}
          disabled={loading}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left"
        />
      </div>

      <div className="flex flex-col gap-2 text-left">
        <label className="flex items-center gap-2 text-left">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            disabled={loading}
            className="w-4 h-4"
          />
          <span className="text-body-bold font-body-bold text-default-font">
            Active
          </span>
        </label>
      </div>

      <div className="flex gap-2 mt-4">
        <Button type="submit" variant="brand-primary" loading={loading}>
          {initialData ? "Update" : "Create"}
        </Button>
        <Button
          type="button"
          variant="neutral-secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};
